import { Competitor, TennisScore } from '../../types/types';
import { GameLog, PairOfCounts, PairOfLabels } from '../../utils/tennisScore';

/** Chakra colour scheme of each player, the same everywhere on the board. */
export const PLAYER_SCHEMES = ['blue', 'green'] as const;

export interface BoardPlayer {
  competitor: Competitor;
  scheme: typeof PLAYER_SCHEMES[number];
  side: keyof PairOfCounts;
}

export const boardPlayers = (first: Competitor, second: Competitor): [BoardPlayer, BoardPlayer] => [
  { competitor: first, scheme: PLAYER_SCHEMES[0], side: 'first' },
  { competitor: second, scheme: PLAYER_SCHEMES[1], side: 'second' },
];

export const pairText = (pair: PairOfLabels | PairOfCounts): string => `${pair.first}-${pair.second}`;

/** Elapsed time as m:ss. */
export const minutesSeconds = (ms: number): string => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/** A finished set: games per player, the winner and, after a tiebreak, its final points. */
export interface BoardSet {
  set: number;
  games: PairOfCounts;
  winnerCompetitorId?: number;
  tiebreakPoints?: PairOfCounts;
}

/** The finished sets of the latest state, with the tiebreak points the game log holds. */
export const boardSets = (latest: TennisScore, log: GameLog, firstId: number, secondId: number): BoardSet[] =>
  latest.completedSets.map(set => {
    const games = (id: number) => set.scores.find(s => s.competitorId === id)?.games ?? 0;
    return {
      set: set.set,
      games: { first: games(firstId), second: games(secondId) },
      winnerCompetitorId: set.winnerCompetitorId,
      tiebreakPoints: log.games.find(g => g.set === set.set && g.tiebreak)?.points,
    };
  });

/** A set's score from the winner's side, the tiebreak loser's points in brackets: 7-6(6). */
export const setScore = (set: BoardSet, players: [BoardPlayer, BoardPlayer]): string => {
  const winner = players.find(p => p.competitor.id === set.winnerCompetitorId) ?? players[0];
  const loser = players.find(p => p !== winner) ?? players[1];
  const tiebreak = set.tiebreakPoints ? `(${set.tiebreakPoints[loser.side]})` : '';
  return `${set.games[winner.side]}-${set.games[loser.side]}${tiebreak}`;
};
